import { tempoApi } from '@/lib/api/tempoApi';
import type { Plan, PlanCreateRequest, PlanUpdateRequest } from '@/lib/api/types/adaptivePlanner';
import type {
  PlanAlert,
  PlanNamesByIdsResponse,
  PlanOwnerBulkFetchResponse,
  PlanOwnerResponse,
  PlanUsersPermissionResponse,
  PlanUsersPermissionsSubmitData,
  SyncWithPortfolioResponse,
  ValidateOwnerTransferResponse,
} from '../types/adaptive-planner/plan';

export const getPlans = async ({ limit }: { limit?: number } = {}): Promise<Plan[]> => {
  const searchParams = new URLSearchParams();

  if (limit !== undefined) {
    searchParams.set('limit', limit.toString());
  }

  const url = `api/v1/adaptive-planner/plans${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  const response = await tempoApi().get(url).json<Plan[]>();
  return response;
};

const getPlan = async ({ planId }: { planId: number }): Promise<Plan> => {
  const response = await tempoApi().get(`api/v1/adaptive-planner/plans/${planId}`).json<Plan>();
  return response;
};

const createPlan = async ({ data }: { data: PlanCreateRequest }): Promise<Plan> => {
  const response = await tempoApi()
    .post('api/v1/adaptive-planner/plans', {
      json: data,
    })
    .json<Plan>();
  return response;
};

const updatePlan = async ({
  planId,
  data,
}: {
  planId: number;
  data: PlanUpdateRequest;
}): Promise<Plan> => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/plans/${planId}`, { json: data })
    .json<Plan>();
  return response;
};

const deletePlan = async ({ planId }: { planId: number }): Promise<void> => {
  await tempoApi().delete(`api/v1/adaptive-planner/plans/${planId}`);
};

const getUsersPermissionsForPlan = async ({
  planId,
}: {
  planId: number;
}): Promise<PlanUsersPermissionResponse> => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/permissions`)
    .json<PlanUsersPermissionResponse>();
  return response;
};

const setUsersPermissionsForPlan = async ({
  planId,
  data,
}: {
  planId: number;
  data: PlanUsersPermissionsSubmitData;
}) => {
  const response = await tempoApi()
    .put(`api/v1/adaptive-planner/plans/${planId}/permissions`, {
      json: data,
    })
    .json<void>();

  return response;
};

const syncWithPortfolio = async ({
  planId,
}: {
  planId: number;
}): Promise<SyncWithPortfolioResponse> => {
  return await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/portfolio-sync`)
    .json<SyncWithPortfolioResponse>();
};

const getAlerts = async ({
  planId,
  alertTypes,
}: {
  planId: number;
  alertTypes?: PlanAlert['alertType'][];
}): Promise<PlanAlert[]> => {
  const searchParams = new URLSearchParams();

  if (alertTypes && alertTypes.length > 0) {
    alertTypes.forEach((type) => {
      searchParams.append('alertTypes', type);
    });
  }

  const url = `api/v1/adaptive-planner/plans/${planId}/alerts${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  return await tempoApi().get(url).json<PlanAlert[]>();
};

const getPlanOwner = async ({ planId }: { planId: number }): Promise<PlanOwnerResponse> => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/owner`)
    .json<PlanOwnerResponse>();
  return response;
};

const updatePlanOwner = async ({
  planId,
  userId,
}: {
  planId: number;
  userId: string;
}): Promise<void> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/owner`, {
      json: { userId },
    })
    .json<void>();
  return response;
};

const getPlanOwnerBulkFetch = async ({
  planIds,
}: {
  planIds: number[];
}): Promise<PlanOwnerBulkFetchResponse> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/owners/bulk`, {
      json: { planIds },
    })
    .json<PlanOwnerBulkFetchResponse>();
  return response;
};

const getPlanNamesByIds = async (ids: number[]): Promise<PlanNamesByIdsResponse> => {
  const response = await tempoApi()
    .post('api/v1/adaptive-planner/plans/names-by-ids', {
      json: { ids },
    })
    .json<PlanNamesByIdsResponse>();
  return response;
};

const validateOwnerTransfer = async ({
  planId,
  userId,
}: {
  planId: number;
  userId: string;
}): Promise<ValidateOwnerTransferResponse> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/owner/validate`, {
      json: { userId },
    })
    .json<ValidateOwnerTransferResponse>();
  return response;
};

export const AdaptivePlannerPlan = {
  getPlan,
  getPlans,
  updatePlan,
  deletePlan,
  createPlan,
  getUsersPermissionsForPlan,
  setUsersPermissionsForPlan,
  syncWithPortfolio,
  getAlerts,
  getPlanOwner,
  updatePlanOwner,
  getPlanOwnerBulkFetch,
  getPlanNamesByIds,
  validateOwnerTransfer,
} as const;
